/**
 * Service d'impression et d'export PDF — 100 % hors ligne.
 * jsPDF et html2canvas sont bundlés par Vite (aucun CDN), conformément à la
 * contrainte de fonctionnement local du desktop. Le CSS de thème est injecté
 * verbatim dans la fenêtre d'impression pour reproduire la charte v2.2.
 */
import themeCss from '@presentation/styles/nexxus-theme.css?raw';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { ToastService } from '@presentation/components/Toast';

/** Compression de l'image dans le PDF (sans perte). */
const COMPRESSION_IMAGE = 'MEDIUM';

/** Rend un nœud DOM en PDF A4 (paginé si nécessaire) et retourne le blob. */
async function nodeToPdfBlob(node: HTMLElement): Promise<Blob> {
  const canvas = await html2canvas(node, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
  });
  return canvasToPdfBlob(canvas);
}

/**
 * Insère l'image de la page dans un PDF A4. L'image reste en PNG (sans perte : logo net, texte
 * lisible) mais est COMPRESSÉE dans le PDF : sans cela, jsPDF l'écrit en pixels bruts
 * (environ 7,8 Mo par bulletin au lieu de quelques centaines de Ko).
 */
export async function canvasToPdfBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pdfW = pdf.internal.pageSize.getWidth();
  const pdfH = pdf.internal.pageSize.getHeight();
  const imgH = (pdfW * canvas.height) / canvas.width;
  if (imgH <= pdfH) {
    pdf.addImage(imgData, 'PNG', 0, 0, pdfW, imgH, undefined, COMPRESSION_IMAGE);
  } else {
    let position = 0;
    while (position < imgH) {
      pdf.addImage(imgData, 'PNG', 0, -position, pdfW, imgH, undefined, COMPRESSION_IMAGE);
      position += pdfH;
      if (position < imgH) pdf.addPage();
    }
  }
  return pdf.output('blob');
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Génère et télécharge un PDF à partir d’un fragment HTML (bulletin, attestation…). */
export async function downloadHtmlAsPdf(html: string, filename: string): Promise<void> {
  const host = document.createElement('div');
  host.style.cssText = 'position:fixed;left:-9999px;top:0;width:820px;background:#fff;';
  host.innerHTML = html;
  document.body.appendChild(host);
  try {
    const node = host.firstElementChild as HTMLElement | null;
    if (!node) throw new Error('HTML vide');
    const blob = await nodeToPdfBlob(node);
    downloadBlob(blob, filename);
  } finally {
    host.remove();
  }
}

/** Rend un nœud DOM en PDF A4 (paginé si nécessaire) et déclenche le téléchargement. */
export async function elementToPdf(node: HTMLElement, filename: string): Promise<void> {
  ToastService.info('Génération du PDF en cours…');
  try {
    const blob = await nodeToPdfBlob(node);
    downloadBlob(blob, filename);
    ToastService.success(`PDF téléchargé : ${filename}`);
  } catch (err) {
    ToastService.error('Échec de génération du PDF — utilisez l’impression navigateur');
    throw err;
  }
}

const PRINT_OVERRIDES = `
  body { background:#ECEFF1; margin:0; padding:16px; font-family:"Calibri","Segoe UI",Arial,sans-serif; }
  .batch-toolbar { position:sticky; top:0; z-index:10; background:#0B2545; color:#fff;
    padding:10px 16px; margin:-16px -16px 16px -16px; display:flex; justify-content:space-between;
    align-items:center; font-size:13px; }
  .batch-toolbar button { background:#1F7A8C; color:#fff; border:0; padding:8px 14px;
    border-radius:6px; cursor:pointer; font-weight:600; }
  .bulletin-page { margin:0 auto 20px auto; max-width:800px; }
  .bulletin { box-shadow:0 4px 16px rgba(0,0,0,0.08); }
  @media print {
    @page { size:A4 portrait; margin:0; }
    html, body { background:#fff!important; padding:0!important; margin:0!important; }
    * { -webkit-print-color-adjust:exact!important; print-color-adjust:exact!important; }
    /* Le thème masque « body * » à l'impression (réservé aux seules attestations et CV) : sans cette
       neutralisation, le bulletin, l'impression en lot, les factures et les rapports sortent BLANCS. */
    body * { visibility:visible!important; }
    .batch-toolbar { display:none!important; }
    .bulletin-page, .nx-print-page { margin:0!important; max-width:100%; padding:0.5in; box-sizing:border-box;
      page-break-after:always; break-after:page; page-break-inside:avoid; break-inside:avoid; }
    .bulletin-page:last-child, .nx-print-page:last-child { page-break-after:auto; break-after:auto; }
    .bulletin { max-width:100%!important; border:0!important; box-shadow:none!important; }
  }
`;

/** Ouvre une fenêtre d'impression contenant le HTML fourni, stylé par le thème. */
export function printHtml(innerHtml: string, toolbarLabel = ''): void {
  const win = window.open('', '_blank', 'width=900,height=1100');
  if (!win) {
    ToastService.error("Impossible d'ouvrir la fenêtre d'impression (popup bloqué ?)");
    return;
  }
  const toolbar = toolbarLabel
    ? `<div class="batch-toolbar"><span>${toolbarLabel}</span><button id="nx-print-btn" type="button">Imprimer</button></div>`
    : '';
  win.document.open();
  win.document.write(`<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title> </title>
    <style>${themeCss}</style><style>${PRINT_OVERRIDES}</style></head><body>${toolbar}${innerHtml}</body></html>`);
  win.document.close();
  // Fenêtre d'impression : le bouton est branché ici (aucun gestionnaire en ligne, la CSP les interdit).
  win.document.getElementById('nx-print-btn')?.addEventListener('click', () => {
    win.print();
  });
  const trigger = (): void => {
    try {
      win.focus();
      win.print();
    } catch {
      /* ignore */
    }
  };
  if (win.document.readyState === 'complete') setTimeout(trigger, 400);
  else
    win.onload = (): void => {
      setTimeout(trigger, 400);
    };
}

/**
 * Affiche un aperçu plein écran d'un document (bulletin/attestation) avec
 * boutons Imprimer / Télécharger PDF / Fermer. L'aperçu vit dans le document
 * principal afin que jsPDF/html2canvas accèdent au thème chargé.
 */
export function previewDocument(docHtml: string, filename: string, title = 'Document'): void {
  const existing = document.getElementById('nx-doc-overlay');
  existing?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'nx-doc-overlay';
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:9999;background:rgba(10,18,30,.55);display:flex;flex-direction:column;';
  overlay.innerHTML = `
    <div style="background:#0B2545;color:#fff;padding:10px 18px;display:flex;justify-content:space-between;align-items:center;gap:12px;">
      <strong style="font-size:14px;">${title}</strong>
      <div style="display:flex;gap:8px;">
        <button data-act="print" style="background:#1F7A8C;color:#fff;border:0;padding:8px 14px;border-radius:6px;cursor:pointer;font-weight:600;">Imprimer</button>
        <button data-act="pdf" style="background:#00B4C8;color:#0A121E;border:0;padding:8px 14px;border-radius:6px;cursor:pointer;font-weight:600;">Télécharger PDF</button>
        <button data-act="close" style="background:transparent;color:#fff;border:1px solid rgba(255,255,255,.4);padding:8px 14px;border-radius:6px;cursor:pointer;">Fermer</button>
      </div>
    </div>
    <div style="flex:1;overflow:auto;padding:24px;display:flex;justify-content:center;">
      <div id="nx-doc-host" style="max-width:820px;width:100%;">${docHtml}</div>
    </div>`;
  document.body.appendChild(overlay);

  const close = (): void => overlay.remove();
  overlay.querySelector('[data-act="close"]')?.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector('[data-act="print"]')?.addEventListener('click', () => {
    const host = overlay.querySelector('#nx-doc-host');
    if (host) printHtml(`<div class="nx-print-page">${host.innerHTML}</div>`);
  });
  overlay.querySelector('[data-act="pdf"]')?.addEventListener('click', () => {
    const node = overlay.querySelector<HTMLElement>('#nx-doc-host > *');
    if (node) void elementToPdf(node, filename);
  });
}
